import React, { useState } from 'react'
import styled from 'styled-components'
import { useTheme } from '../context/ThemeContext'
import { Bell, Megaphone, Sparkles, Wrench, AlertCircle, ChevronRight, Search, Pin } from 'lucide-react'

const Container = styled.div`
  padding: 32px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 22px;
`

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0;
`

const Subtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 18px;
  flex-wrap: wrap;
`

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;

  svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: ${props => props.theme.textMuted};
    pointer-events: none;
  }
  input {
    width: 100%;
    padding: 12px 16px 12px 42px;
    border: 1px solid ${props => props.theme.border};
    border-radius: 10px;
    background: ${props => props.theme.cardBackground};
    font-size: 14px;
    color: ${props => props.theme.textPrimary};
    &:focus {
      outline: none;
      border-color: #7c3aed;
      box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
    }
    &::placeholder { color: ${props => props.theme.textMuted}; }
  }
`

const FilterTabs = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const FilterTab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: 1px solid ${props => props.$active ? '#7c3aed' : props.theme.border};
  border-radius: 999px;
  background: ${props => props.$active ? 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(37,99,235,0.10))' : props.theme.cardBackground};
  color: ${props => props.$active ? '#7c3aed' : props.theme.textSecondary};
  font-size: 13px;
  font-weight: ${props => props.$active ? 700 : 500};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #7c3aed;
    color: #7c3aed;
  }
`

const NoticeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const NoticeCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
  padding: 18px 20px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #7c3aed;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(124,58,237,0.10);
  }
`

const IconBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.$bg || 'rgba(124,58,237,0.10)'};
  color: ${props => props.$color || '#7c3aed'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const NoticeBody = styled.div`
  flex: 1;
  min-width: 0;
`

const NoticeTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`

const CategoryTag = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${props => props.$bg || 'rgba(124,58,237,0.10)'};
  color: ${props => props.$color || '#7c3aed'};
`

const PinBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: rgba(245,158,11,0.12);
  color: #d97706;
`

const NoticeTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 4px 0;
  line-height: 1.4;
`

const NoticePreview = styled.p`
  font-size: 13px;
  color: ${props => props.theme.textSecondary};
  margin: 0 0 8px 0;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const NoticeMeta = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textMuted};
`

const Chev = styled.div`
  align-self: center;
  color: ${props => props.theme.textMuted};
  flex-shrink: 0;
`

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${props => props.theme.textSecondary};
  font-size: 14px;
`

const CATEGORIES = {
  all: { label: '전체', Icon: Bell, bg: 'rgba(124,58,237,0.10)', color: '#7c3aed' },
  notice: { label: '공지', Icon: Megaphone, bg: 'rgba(37,99,235,0.10)', color: '#2563eb' },
  update: { label: '업데이트', Icon: Sparkles, bg: 'rgba(16,185,129,0.10)', color: '#10b981' },
  maintenance: { label: '점검', Icon: Wrench, bg: 'rgba(245,158,11,0.12)', color: '#d97706' },
  important: { label: '중요', Icon: AlertCircle, bg: 'rgba(239,68,68,0.10)', color: '#ef4444' },
}

const NOTICES = [
  {
    id: 6,
    category: 'update',
    pinned: true,
    title: '[v1.5.0] 보관함·오브젝트 관리 디자인 개편',
    preview: '보관함과 오브젝트 관리 페이지의 탭/카드 스타일을 통일하고, 설정 페이지를 새로 디자인했습니다.',
    date: '2026-05-01',
    author: '운영팀',
  },
  {
    id: 5,
    category: 'notice',
    pinned: true,
    title: 'AR 대탈출 모바일 앱 정식 출시 안내',
    preview: 'Google Play에서 AR 대탈출 앱을 다운로드하실 수 있습니다. QR 코드를 통해 빠르게 설치해보세요.',
    date: '2026-04-30',
    author: '운영팀',
  },
  {
    id: 4,
    category: 'important',
    pinned: false,
    title: '비공개 퀴즈 필터링 기능 안내',
    preview: '비공개로 설정된 퀴즈는 이제 방 목록에서 자동으로 필터링됩니다. 본인이 만든 퀴즈는 보관함에서 확인 가능합니다.',
    date: '2026-04-28',
    author: '운영팀',
  },
  {
    id: 3,
    category: 'update',
    pinned: false,
    title: '문제 만들기 4단계 워크플로우 도입',
    preview: '기본 정보 → 오브젝트 등록 → 문제 만들기 → 배포하기 단계로 구성된 새로운 퀴즈 제작 흐름을 적용했습니다.',
    date: '2026-04-25',
    author: '운영팀',
  },
  {
    id: 2,
    category: 'maintenance',
    pinned: false,
    title: '데이터베이스 정기 점검 안내',
    preview: '매주 화요일 새벽 03:00~04:00 사이 약 30분간 서비스 이용에 제한이 있을 수 있습니다.',
    date: '2026-04-20',
    author: '운영팀',
  },
  {
    id: 1,
    category: 'notice',
    pinned: false,
    title: 'AR 대탈출 베타 서비스 오픈',
    preview: '안녕하세요! AR 대탈출 베타 서비스에 오신 것을 환영합니다. 더 좋은 서비스를 위해 소중한 의견을 들려주세요.',
    date: '2026-04-15',
    author: '운영팀',
  },
]

export default function NoticePage() {
  const { theme } = useTheme()
  const [activeCategory, setActiveCategory] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')

  const filtered = NOTICES.filter(n => {
    if (activeCategory !== 'all' && n.category !== activeCategory) return false
    if (searchTerm.trim() && !n.title.toLowerCase().includes(searchTerm.toLowerCase())
      && !n.preview.toLowerCase().includes(searchTerm.toLowerCase())) return false
    return true
  }).sort((a, b) => {
    if (a.pinned !== b.pinned) return b.pinned - a.pinned
    return new Date(b.date) - new Date(a.date)
  })

  return (
    <Container theme={theme}>
      <PageHeader>
        <Title theme={theme}>공지사항</Title>
        <Subtitle theme={theme}>AR 대탈출의 새로운 소식과 업데이트 내역을 확인하세요.</Subtitle>
      </PageHeader>

      <Toolbar>
        <SearchWrap theme={theme}>
          <Search size={16} />
          <input
            type="text"
            placeholder="공지사항 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchWrap>
      </Toolbar>

      <Toolbar>
        <FilterTabs>
          {Object.entries(CATEGORIES).map(([key, cat]) => {
            const Icon = cat.Icon
            return (
              <FilterTab
                key={key}
                theme={theme}
                $active={activeCategory === key}
                onClick={() => setActiveCategory(key)}
              >
                <Icon size={14} />
                {cat.label}
              </FilterTab>
            )
          })}
        </FilterTabs>
      </Toolbar>

      {filtered.length === 0 ? (
        <EmptyState theme={theme}>해당하는 공지사항이 없습니다.</EmptyState>
      ) : (
        <NoticeList>
          {filtered.map(notice => {
            const cat = CATEGORIES[notice.category] || CATEGORIES.notice
            const Icon = cat.Icon
            return (
              <NoticeCard
                key={notice.id}
                theme={theme}
                onClick={() => alert('상세 보기 준비 중입니다.')}
              >
                <IconBadge $bg={cat.bg} $color={cat.color}>
                  <Icon size={18} />
                </IconBadge>
                <NoticeBody>
                  <NoticeTopRow>
                    {notice.pinned && (
                      <PinBadge>
                        <Pin size={10} /> 고정
                      </PinBadge>
                    )}
                    <CategoryTag $bg={cat.bg} $color={cat.color}>{cat.label}</CategoryTag>
                  </NoticeTopRow>
                  <NoticeTitle theme={theme}>{notice.title}</NoticeTitle>
                  <NoticePreview theme={theme}>{notice.preview}</NoticePreview>
                  <NoticeMeta theme={theme}>
                    {notice.author} · {notice.date}
                  </NoticeMeta>
                </NoticeBody>
                <Chev theme={theme}>
                  <ChevronRight size={18} />
                </Chev>
              </NoticeCard>
            )
          })}
        </NoticeList>
      )}
    </Container>
  )
}
